import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import Home from '../pages/home/page';
import ProtectedRoute from '../components/feature/ProtectedRoute';
import AdminProtectedRoute from '../components/feature/AdminProtectedRoute';

// 路由级代码分割：首屏仅加载首页，其余页面按需加载。
const NotFound = lazy(() => import('../pages/NotFound'));
const SingleAnalysis = lazy(() => import('../pages/single-analysis/page'));
const AuthPage = lazy(() => import('../pages/auth/page'));
const MyAnalysis = lazy(() => import('../pages/my-analysis/page'));
const ComingSoonPage = lazy(() => import('../pages/coming-soon/page'));
const LeadsPage = lazy(() => import('../pages/leads/page'));
const LeadDetailPage = lazy(() => import('../pages/leads/detail/page'));
const MyFavoritesPage = lazy(() => import('../pages/my/favorites/page'));
const MyClientsPage = lazy(() => import('../pages/my/clients/page'));
const AdminLoginPage = lazy(() => import('../pages/admin/login/page'));
const AdminShell = lazy(() => import('../pages/admin/AdminShell'));
const AdminUsersPage = lazy(() => import('../pages/admin/users/page'));
const AdminUserDetailPage = lazy(() => import('../pages/admin/users/detail/page'));
const AdminAuditLogsPage = lazy(() => import('../pages/admin/audit-logs/page'));

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

function protect(node: ReactNode) {
  return withSuspense(<ProtectedRoute>{node}</ProtectedRoute>);
}

function protectAdmin(node: ReactNode) {
  return withSuspense(<AdminProtectedRoute>{node}</AdminProtectedRoute>);
}

// eslint-disable-next-line react-refresh/only-export-components -- 路由文件内局部 fallback
function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50">
      <div className="text-gray-400 text-sm">加载中…</div>
    </div>
  );
}

/** 现网主路由；旧原型页已删除，历史路径重定向到 coming-soon / leads */
const routes: RouteObject[] = [
  { path: '/', element: <Home /> },
  { path: '/auth', element: withSuspense(<AuthPage />) },
  { path: '/admin/login', element: withSuspense(<AdminLoginPage />) },
  {
    path: '/admin',
    element: protectAdmin(<AdminShell />),
    children: [
      { index: true, element: <Navigate to="/admin/users" replace /> },
      { path: 'users', element: withSuspense(<AdminUsersPage />) },
      { path: 'users/:id', element: withSuspense(<AdminUserDetailPage />) },
      { path: 'audit-logs', element: withSuspense(<AdminAuditLogsPage />) },
    ],
  },
  { path: '/leads', element: withSuspense(<LeadsPage />) },
  { path: '/leads/:id', element: protect(<LeadDetailPage />) },
  { path: '/coming-soon/:featureKey', element: withSuspense(<ComingSoonPage />) },
  { path: '/my-analysis', element: protect(<MyAnalysis />) },
  { path: '/my/favorites', element: protect(<MyFavoritesPage />) },
  { path: '/my/clients', element: protect(<MyClientsPage />) },
  { path: '/batch-analysis', element: <Navigate to="/coming-soon/batch" replace /> },
  { path: '/single-analysis', element: withSuspense(<SingleAnalysis />) },
  // 旧原型入口 → 待开发 / 现网线索
  { path: '/opportunity-leads', element: <Navigate to="/leads" replace /> },
  { path: '/opportunity-list/:clientId', element: <Navigate to="/leads" replace /> },
  { path: '/opportunity-report/:clientId', element: <Navigate to="/leads" replace /> },
  { path: '/opportunity/:clientId/:categoryKey', element: <Navigate to="/leads" replace /> },
  { path: '/deep-mining', element: <Navigate to="/coming-soon/supplement" replace /> },
  { path: '/think-tank/:clientId', element: <Navigate to="/coming-soon/thinktank" replace /> },
  { path: '/tax-bp/:id/*', element: <Navigate to="/coming-soon/bp" replace /> },
  { path: '/tax-bp/:id', element: <Navigate to="/coming-soon/bp" replace /> },
  { path: '/tax-bp-report/:id', element: <Navigate to="/coming-soon/bp" replace /> },
  { path: '/strategy', element: <Navigate to="/coming-soon/boss" replace /> },
  { path: '/digital-workbench/:clientId', element: <Navigate to="/coming-soon/boss" replace /> },
  { path: '/business-assistant/:clientId', element: <Navigate to="/coming-soon/boss" replace /> },
  { path: '/client/:id', element: <Navigate to="/leads" replace /> },
  { path: '/policy-preview/:id', element: <Navigate to="/leads" replace /> },
  { path: '*', element: withSuspense(<NotFound />) },
];

export default routes;
